import { useEffect } from 'react'
import {
  Room,
  RoomEvent,
  Track,
  type LocalTrackPublication,
  type RemoteParticipant,
} from 'livekit-client'

import { clientLogError, clientLogInfo } from '@/src/lib/clientLog'
import {
  CODEC_SWITCH_COMPLAINT_WINDOW_MS,
  CODEC_SWITCH_COOLDOWN_MS,
  MAX_CODEC_SWITCHES_PER_TRACK,
  SCREEN_SHARE_CODEC_TOPIC,
  countLiveScreenShareViewers,
  decodeScreenShareCodecMessage,
  encodeScreenShareCodecMessage,
  isAgentLikeParticipant,
  noteCodecSwitchSettled,
  noteCodecSwitchStarting,
  republishScreenShareAsVp8,
  requiredComplaints,
} from '@/src/lib/screenShareCodec'

/** Зрители могут начать наблюдать экран в разное время. */
const FIRST_FRAME_COMPLAINT_WINDOW_MS = 10_000

type SwitchRecord = {
  count: number
  lastAt: number | null
}

export type UseScreenShareCodecSwitchOptions = {
  room?: Room
  enabled?: boolean
}

/**
 * Принимает жалобы зрителей и тихо переводит свой экран на VP8.
 *
 * Порог жалоб зависит от числа живых зрителей трека: маленькая комната чинится
 * по первой жалобе, большая — только по двум от разных зрителей. Сам переход —
 * переиздание того же `MediaStreamTrack`, диалог выбора окна не открывается,
 * раскладка зрителей прикрыта заморозкой автопина.
 *
 * @param options.room - комната livekit, пока undefined хук неактивен
 * @param options.enabled - выключатель, по умолчанию включён
 */
export function useScreenShareCodecSwitch({
  room,
  enabled = true,
}: UseScreenShareCodecSwitchOptions): void {
  useEffect(() => {
    if (!room || !enabled) {
      return
    }

    // Жалобы по трекам: sid демонстрации -> зритель -> время жалобы.
    const complaints = new Map<string, Map<string, number>>()
    // Счётчик переходов: sid демонстрации -> попытки и время последней.
    const switches = new Map<string, SwitchRecord>()
    // После переиздания sid меняется, а захваченный трек остаётся тем же.
    const firstFrameSwitches = new WeakSet<MediaStreamTrack>()

    /**
     * Рассылает итог перехода всем: жалобы по старому треку прекращаются.
     */
    const broadcastSwitched = async (message: {
      oldTrackSid: string
      newTrackSid: string | null
      publisherIdentity: string
      complaintsCount: number
      ok: boolean
    }): Promise<void> => {
      await room.localParticipant.publishData(
        encodeScreenShareCodecMessage({
          type: 'SCREEN_SHARE_CODEC_SWITCHED',
          oldTrackSid: message.oldTrackSid,
          newTrackSid: message.newTrackSid,
          publisherIdentity: message.publisherIdentity,
          codecFrom: 'h264',
          codecTo: 'vp8',
          complaintsCount: message.complaintsCount,
          ok: message.ok,
        }),
        { reliable: true, topic: SCREEN_SHARE_CODEC_TOPIC },
      )
    }

    /**
     * Выполняет переход: предупреждение, переиздание трека и итог всем.
     */
    const runSwitch = async (trackSid: string, complaintsCount: number): Promise<void> => {
      const identity = room.localParticipant.identity
      const now = Date.now()
      const record = switches.get(trackSid) ?? { count: 0, lastAt: null }

      if (record.count >= MAX_CODEC_SWITCHES_PER_TRACK) {
        return
      }

      if (record.lastAt !== null && now - record.lastAt < CODEC_SWITCH_COOLDOWN_MS) {
        return
      }

      record.count += 1
      record.lastAt = now
      switches.set(trackSid, record)

      noteCodecSwitchStarting(identity)

      try {
        await room.localParticipant.publishData(
          encodeScreenShareCodecMessage({
            type: 'SCREEN_SHARE_CODEC_SWITCHING',
            oldTrackSid: trackSid,
            publisherIdentity: identity,
          }),
          { reliable: true, topic: SCREEN_SHARE_CODEC_TOPIC },
        )
      } catch {
        // Предупреждение не ушло, а переход всё равно нужен — идём дальше
      }

      clientLogInfo('screen-share-codec-switch-start', {
        room: room.name,
        trackSid,
        publisher: identity,
        complaintsCount,
        attempt: record.count,
      })

      try {
        const result = await republishScreenShareAsVp8(room, trackSid)

        if (!result) {
          throw new Error('Нет живого видеотрека для переиздания в VP8')
        }

        await broadcastSwitched({
          oldTrackSid: result.oldTrackSid,
          newTrackSid: result.newTrackSid,
          publisherIdentity: identity,
          complaintsCount,
          ok: true,
        })

        clientLogInfo('screen-share-codec-switch-done', {
          room: room.name,
          oldTrackSid: result.oldTrackSid,
          newTrackSid: result.newTrackSid,
          publisher: identity,
          complaintsCount,
          codecFrom: 'h264',
          codecTo: 'vp8',
        })
      } catch (error) {
        clientLogError('screen-share-codec-switch-failed', {
          room: room.name,
          trackSid,
          publisher: identity,
          error: error instanceof Error ? error.message : String(error),
        })

        try {
          await broadcastSwitched({
            oldTrackSid: trackSid,
            newTrackSid: null,
            publisherIdentity: identity,
            complaintsCount,
            ok: false,
          })
        } catch {
          // Итог не разослать — зрители отработают по таймауту заморозки пина
        }
      } finally {
        noteCodecSwitchSettled(identity)
      }
    }

    /**
     * Копит жалобы и запускает переход, когда хватает голосов.
     */
    const handleDataReceived = (payload: Uint8Array, participant?: RemoteParticipant): void => {
      const message = decodeScreenShareCodecMessage(payload)

      if (!message || message.type !== 'SCREEN_SHARE_CODEC_COMPLAINT') {
        return
      }

      const myPublication = room.localParticipant.getTrackPublication(Track.Source.ScreenShare)

      // Чужая демонстрация или устаревший трек — жалоба не нам.
      if (
        !myPublication ||
        myPublication.trackSid !== message.trackSid ||
        message.publisherIdentity !== room.localParticipant.identity
      ) {
        return
      }

      if (!participant || participant.identity === room.localParticipant.identity) {
        return
      }

      if (
        message.reason === 'first-frame-timeout' &&
        (isAgentLikeParticipant(participant) || participant.permissions?.recorder === true)
      ) {
        return
      }

      const codec = myPublication.options?.videoCodec ?? myPublication.videoTrack?.codec ??
        myPublication.mimeType?.toLowerCase().replace('video/', '')
      const mediaTrack = myPublication.track?.mediaStreamTrack

      // Не переиздаём VP8 и не меняем неизвестный кодек по одному таймауту кадра.
      if (
        codec === 'vp8' ||
        (message.reason === 'first-frame-timeout' &&
          (codec !== 'h264' || !mediaTrack || firstFrameSwitches.has(mediaTrack)))
      ) {
        return
      }

      const now = Date.now()
      let byViewer = complaints.get(message.trackSid)

      if (!byViewer) {
        byViewer = new Map()
        complaints.set(message.trackSid, byViewer)
      }

      byViewer.set(participant.identity, now)

      const complaintWindow = message.reason === 'first-frame-timeout'
        ? FIRST_FRAME_COMPLAINT_WINDOW_MS
        : CODEC_SWITCH_COMPLAINT_WINDOW_MS

      byViewer.forEach((sentAt, viewer) => {
        if (now - sentAt > complaintWindow) {
          byViewer?.delete(viewer)
        }
      })

      const viewerCount = countLiveScreenShareViewers(room, message.trackSid)
      let possibleViewerCount = 0

      if (message.reason === 'first-frame-timeout') {
        // SDK не показывает публикатору подписки на его трек, поэтому берём верхнюю оценку.
        room.remoteParticipants.forEach((remote) => {
          if (
            !isAgentLikeParticipant(remote) &&
            remote.permissions?.recorder !== true &&
            remote.permissions?.canSubscribe !== false
          ) {
            possibleViewerCount += 1
          }
        })
      }

      const required = requiredComplaints(Math.max(viewerCount, possibleViewerCount))

      clientLogInfo('screen-share-codec-complaint-received', {
        room: room.name,
        trackSid: message.trackSid,
        viewer: participant.identity,
        distinctComplaints: byViewer.size,
        reason: message.reason ?? 'undecodable',
        requiredComplaints: required,
        viewerCount,
        possibleViewerCount,
      })

      if (byViewer.size < required) {
        return
      }

      complaints.delete(message.trackSid)

      if (message.reason === 'first-frame-timeout' && mediaTrack) {
        firstFrameSwitches.add(mediaTrack)
      }

      void runSwitch(message.trackSid, byViewer.size)
    }

    /**
     * Новая демонстрация обнуляет старые жалобы: голоса были про другой трек.
     */
    const handleLocalTrackPublished = (publication: LocalTrackPublication): void => {
      if (publication.source === Track.Source.ScreenShare) {
        complaints.clear()
      }
    }

    room
      .on(RoomEvent.DataReceived, handleDataReceived)
      .on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished)

    return () => {
      room
        .off(RoomEvent.DataReceived, handleDataReceived)
        .off(RoomEvent.LocalTrackPublished, handleLocalTrackPublished)

      complaints.clear()
    }
  }, [room, enabled])
}
